import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState, type FormEvent } from "react";
import { useRoleGuard } from "@/hooks/use-role-guard";
import { useServerFn } from "@tanstack/react-start";
import { UserPlus, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import {
  listTeam,
  inviteAdmin,
  setTeamRole,
  cancelInvite,
  type TeamMember,
} from "@/lib/admin.functions";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/admin/team")({
  head: () => ({
    meta: [
      { title: "Team - Admin - DeckScore" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: TeamPage,
});

function TeamPage() {
  const { status: roleStatus } = useRoleGuard(["super_admin"]);
  const runList = useServerFn(listTeam);
  const runInvite = useServerFn(inviteAdmin);
  const runSetRole = useServerFn(setTeamRole);
  const runCancel = useServerFn(cancelInvite);

  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [inviting, setInviting] = useState(false);
  const [me, setMe] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function refresh() {
    setLoading(true);
    try {
      const { members } = await runList();
      setMembers(members);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    (async () => {
      const { data } = await supabase.auth.getSession();
      setMe(data.session?.user.id ?? null);
    })();
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleInvite(e: FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    setError(null);
    setInviting(true);
    try {
      await runInvite({ data: { email: email.trim() } });
      setEmail("");
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to invite");
    } finally {
      setInviting(false);
    }
  }

  async function handleDeactivate(m: TeamMember) {
    if (!m.id) return;
    if (!confirm(`Remove admin access for ${m.email}?`)) return;
    try {
      await runSetRole({ data: { userId: m.id, role: "founder" } });
      await refresh();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed");
    }
  }

  async function handleCancelInvite(m: TeamMember) {
    if (!confirm(`Cancel invite for ${m.email}?`)) return;
    try {
      await runCancel({ data: { email: m.email } });
      await refresh();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed");
    }
  }

  if (roleStatus !== "ok") return null;
  return (
    <>
      <div className="mb-6">
        <p className="font-script text-accent text-[22px] leading-none">team</p>
        <h1 className="font-display text-[28px] font-semibold mt-2">Team members</h1>
      </div>

      <div className="bg-surface border border-line rounded-2xl p-5 mb-6">
        <form onSubmit={handleInvite} className="flex flex-col sm:flex-row gap-3 sm:items-end">
          <div className="flex-1">
            <label htmlFor="invite-email" className="text-[13px] text-ink-secondary block mb-1.5">
              Add team member
            </label>
            <Input
              id="invite-email"
              type="email"
              placeholder="teammate@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <Button
            type="submit"
            disabled={inviting}
            className="rounded-full bg-accent text-accent-ink hover:bg-accent-press"
          >
            <UserPlus className="w-4 h-4 mr-1" />
            {inviting ? "Inviting…" : "Invite"}
          </Button>
        </form>
        <p className="text-[12px] text-ink-tertiary mt-2">
          They'll get admin access on their next magic-link login.
        </p>
        {error && <p className="text-[13px] text-[#C65C4E] mt-2">{error}</p>}
      </div>

      <div className="bg-surface border border-line rounded-2xl overflow-hidden">
        <table className="w-full text-[14px]">
          <thead className="bg-surface-sunken text-ink-secondary">
            <tr>
              <th className="text-left px-4 py-3 font-medium">Email</th>
              <th className="text-left px-4 py-3 font-medium">Role</th>
              <th className="text-left px-4 py-3 font-medium">Status</th>
              <th className="text-right px-4 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center text-ink-tertiary">
                  Loading…
                </td>
              </tr>
            ) : members.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center text-ink-tertiary">
                  No team members yet.
                </td>
              </tr>
            ) : (
              members.map((m) => {
                const isSelf = m.id && m.id === me;
                return (
                  <tr key={(m.id ?? "invite") + m.email} className="border-t border-line">
                    <td className="px-4 py-3 font-medium">
                      {m.email}
                      {isSelf && <span className="ml-2 text-[11px] text-ink-tertiary">(you)</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-accent-soft text-accent-ink">
                        {m.role === "super_admin" ? "Super admin" : "Admin"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {m.pending ? (
                        <span className="text-[13px] text-ink-tertiary">Pending</span>
                      ) : (
                        <span className="text-[13px] text-ink-secondary">Active</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {m.pending ? (
                        <button
                          onClick={() => handleCancelInvite(m)}
                          className="inline-flex items-center gap-1 text-[13px] text-[#C65C4E] hover:opacity-80 px-2 py-1"
                        >
                          <X className="w-3.5 h-3.5" /> Cancel invite
                        </button>
                      ) : m.role === "admin" && !isSelf ? (
                        <button
                          onClick={() => handleDeactivate(m)}
                          className="inline-flex items-center gap-1 text-[13px] text-[#C65C4E] hover:opacity-80 px-2 py-1"
                        >
                          Deactivate
                        </button>
                      ) : (
                        <span className="text-[13px] text-ink-tertiary">-</span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}
